import React, { useState } from 'react'

import {
  TextField,
  Checkbox,
  Radio,
  CircularProgress,
  MenuItem,
  IconButton
} from '@material-ui/core'
import { KeyboardDatePicker } from '@material-ui/pickers'
import Autocomplete from '@material-ui/lab/Autocomplete'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import InputAdornment from '@material-ui/core/InputAdornment'

import Toggle from '../../Toggle/Toggle'

import classes from './InputGroup.scss'
import TextEditor from '../TextEditor/TextEditor'
import FileInput from '../FileInput/FileInput'
import Formula from '../Formula/Formula'
import ColorPicker from 'material-ui-color-picker'
import withTranslate from 'react-redux-multilingual/lib/withTranslate'
import { Visibility, VisibilityOff } from '@material-ui/icons'

const InputGroup = ({
  hidden,
  variant,
  invalid,
  validations,
  touched,
  validationError,
  groupClass,
  overrideClass,
  labelClass,
  noMargin,
  label = '',
  changed,
  inputValue,
  keyboardIcon,
  configs = {},
  loading,
  inputType,
  disabled,
  formulaSelectValue,
  selectValueChanged,
  radioKey,
  radioValue,
  style,
  translate
}) => {
  const [showPassword, setShowPassword] = useState(false)

  if (hidden) return null

  let validationText = null
  let labelClasses = [classes.InputLabel]
  let groupClasses = [classes.InputGroup]
  let inputElement = null
  let labelElement = null

  label = translate(label.replace('.', '')) || label

  if (variant === 'login') {
    labelClasses = [...labelClasses, classes.Login]
  } else if (variant === 'modal') {
    labelClasses = [...labelClasses, classes.Modal]
    groupClasses = [...groupClasses, classes.Modal]
  }
  if (invalid && validations && touched) {
    validationText = <small className={classes.ValidationText}>{validationError}</small>
  }

  if (groupClass && overrideClass) {
    groupClasses = [groupClass]
  } else if (groupClass) {
    groupClasses = [...groupClasses, groupClass]
  }

  if (labelClass && overrideClass) {
    labelClasses = [labelClass]
  } else if (labelClass) {
    labelClasses = [...labelClasses, labelClass]
  }

  if (noMargin) {
    groupClasses = [...groupClasses, classes.NoMargin]
  }

  if (label) {
    if (validations && validations.required) {
      labelClasses = [...labelClasses, classes.Required]
    }
    labelElement = (
      <div className={classes.InputLabelContainer}>
        <label className={labelClasses.join(' ')}>{label}</label>
      </div>
    )
  }

  switch (inputType) {
    case 'originalSelect':
      let inputOptions = null
      labelElement = null

      if (configs.options && configs.options.length) {
        inputOptions = configs.options.map(item => (
          <MenuItem key={item.label} value={item.value}>
            {item.label}
          </MenuItem>
        ))
      }

      inputElement = (
        <TextField
          select
          label={label}
          onChange={changed}
          value={inputValue}
          variant={variant}
          size={'small'}
          {...configs}>
          {inputOptions}
        </TextField>
      )
      break
    case 'newselect':
      labelElement = null
      inputElement = (
        <Autocomplete
          options={loading ? [] : configs.options}
          getOptionLabel={
            configs?.getOptionLabel ||
            (option => (option.label ? option.label : typeof option === 'string' ? option : ''))
          }
          clearOnEscape
          loading={loading}
          // loadingText={`Loading ...`}
          disableClearable={configs?.clearDisabled}
          freeSolo={configs?.freeSolo}
          value={inputValue}
          onChange={(e, value) => {
            changed(value)
          }}
          disabled={configs?.disabled}
          renderInput={params => {
            let checkClearOnSelect = validations?.required
              ? {
                  ...params.InputProps.endAdornment,
                  props: {
                    ...params.InputProps.endAdornment.props,
                    children: params.InputProps.endAdornment.props.children.filter(
                      c => c?.props?.title !== 'Clear'
                    )
                  }
                }
              : params.InputProps.endAdornment
            return (
              <TextField
                {...params}
                disabled={configs?.disabled}
                variant={
                  ['outlined', 'filled'].includes(configs?.variant || variant)
                    ? configs?.variant || variant
                    : 'standard'
                }
                required={validations?.required}
                label={label}
                fullWidth
                inputProps={{
                  ...params.inputProps,
                  autoComplete: 'none'
                }}
                size={configs?.size}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {loading ? <CircularProgress color='inherit' size={20} /> : null}
                      {checkClearOnSelect}
                    </React.Fragment>
                  )
                }}
              />
            )
          }}
        />
      )
      break
    case 'toggle':
      inputElement = (
        <Toggle
          variant={'inputgroup'}
          checked={inputValue}
          changed={changed}
          disabled={disabled}
          label={label}
        />
      )
      break
    case 'textEditor':
      inputElement = (
        <div className={classes.TextEditor}>
          <TextEditor inputValue={inputValue} changed={changed} />
        </div>
      )
      break
    case 'formula':
      inputElement = (
        <Formula
          configs={configs}
          formulaSelectValue={formulaSelectValue}
          selectValueChanged={selectValueChanged}
          changed={changed}
          inputValue={inputValue}
          validations={validations}
        />
      )
      break
    case 'newmultiSelect':
      labelElement = null

      inputElement = (
        <Autocomplete
          options={loading ? [] : configs.options}
          multiple
          getOptionLabel={option =>
            option.label ? option.label : typeof option === 'string' ? option : ''
          }
          clearOnEscape
          loading={loading}
          // loadingText={`Loading ...`}
          disabled={configs?.disabled}
          value={inputValue}
          onChange={(e, value) => {
            changed(value)
          }}
          {...configs}
          renderInput={params => {
            return (
              <TextField
                {...params}
                label={label}
                required={validations?.required}
                variant={['outlined', 'filled'].includes(variant) ? variant : 'standard'}
                fullWidth
                size={configs?.size}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {loading ? <CircularProgress color='inherit' size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  )
                }}
              />
            )
          }}
        />
      )
      break
    case 'file':
      inputElement = <FileInput changed={changed} selected={inputValue} configs={configs} />
      break
    case 'datePicker':
      labelElement = null
      inputElement = (
        <KeyboardDatePicker
          variant='dialog'
          format='YYYY MM DD'
          label={label ? `${label} ${validations?.required ? '*' : ''}` : null}
          value={inputValue}
          keyboardIcon={keyboardIcon}
          onChange={changed}
          disabled={disabled || configs?.disabled}
          minDate={validations?.minDate}
          maxDate={validations?.maxDate}
          error={validationText !== null}
          helperText={validationText}
          {...configs}
        />
      )

      break
    case 'datePickerYMD':
      labelElement = null
      inputElement = (
        <KeyboardDatePicker
          disableToolbar
          variant='dialog'
          format='YYYY MM DD'
          label={label}
          value={inputValue}
          onChange={changed}
          {...configs}
        />
      )

      break
    case 'checkbox':
      inputElement = (
        <FormControlLabel
          style={style}
          control={
            <Checkbox
              checked={inputValue}
              onChange={changed}
              {...configs}
              value={inputValue === 1}
            />
          }
          label={label}
        />
      )
      labelElement = null
      break
    case 'radio':
      inputElement = (
        <Radio
          type='radio'
          name={radioKey}
          onChange={changed}
          value={radioValue}
          checked={inputValue === radioValue}
          disabled={disabled}
        />
      )
      break
    case 'color':
      labelElement = null
      inputElement = (
        <ColorPicker
          name='color'
          defaultValue='#00000'
          value={inputValue}
          onChange={changed}
          {...configs}
        />
      )
      break
    case 'input':
    default:
      labelElement = null
      let endAdornment = null
      let startAdornment = null
      let type = configs?.type || 'text'

      if (configs?.endAdornment) {
        endAdornment = <InputAdornment position='end'>{configs.endAdornment}</InputAdornment>
      }
      if (configs?.startAdornment) {
        startAdornment = <InputAdornment position='start'>{configs.startAdornment}</InputAdornment>
      }
      if (configs?.type === 'password') {
        endAdornment = (
          <InputAdornment position='end'>
            <IconButton
              aria-label='toggle password visibility'
              onClick={() => setShowPassword(prev => !prev)}>
              {showPassword ? <Visibility /> : <VisibilityOff />}
            </IconButton>
          </InputAdornment>
        )
        type = showPassword ? 'text' : configs?.type
      }
      inputElement = (
        <TextField
          required={validations?.required}
          fullWidth
          label={label}
          onChange={changed}
          value={inputValue}
          error={validationText !== null}
          helperText={validationText}
          variant={['outlined', 'filled'].includes(variant) ? variant : 'standard'}
          InputProps={{ endAdornment: endAdornment, startAdornment: startAdornment }}
          disabled={disabled}
          {...configs}
          type={type}
        />
      )
      // validationText = null

      break
  }

  return (
    <div className={groupClasses.join(' ')}>
      {labelElement}
      {inputElement}
      {/* {validationText} */}
    </div>
  )
}

export default withTranslate(InputGroup)
